import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Coins } from 'lucide-react';
import { API_URL } from '../../services/api';

interface PricingEntry {
  id: string;
  currencyCode: string;
  currencySymbol: string;
  pricePerCredit: number;
  isDefault: boolean;
}

const CURRENCY_OPTIONS = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'DH' },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
    { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: 'SR' },
];
function ScrapePricingCard({ item, onSave }: { item: any; onSave: (id: string, cost: number, label: string) => void }) {
    const [cost, setCost] = useState(item.costPerUnit);
    const [label, setLabel] = useState(item.label);
    
    useEffect(() => {
        setCost(item.costPerUnit);
        setLabel(item.label);
    }, [item]);

    const hasChanges = cost !== item.costPerUnit || label !== item.label;

    return (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/5 space-y-4 relative">
            <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1">Action Name</label>
                <input 
                    type="text"
                    className="w-full bg-transparent border-b border-white/10 text-white font-bold outline-none focus:border-amber-500/50 pb-1"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                />
            </div>
            <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1">Credits per {item.unitName}</label>
                <div className="flex items-center gap-2">
                    <input 
                        type="number"
                        step="0.1"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono outline-none focus:border-amber-500/50"
                        value={cost}
                        onChange={(e) => setCost(parseFloat(e.target.value) || 0)}
                    />
                    <span className="text-gray-500 text-xs font-bold">Credits</span>
                </div>
            </div>
            {hasChanges && (
                <button 
                    onClick={() => onSave(item.id, cost, label)}
                    className="w-full py-2 mt-2 bg-amber-500 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
                >
                    <Save size={14} /> Save Changes
                </button>
            )}
        </div>
    );
}

export default function PricingManagement() {
  const [pricing, setPricing] = useState<PricingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newEntry, setNewEntry] = useState({
    currencyCode: '',
    currencySymbol: '',
    pricePerCredit: 0.12,
    isDefault: false
  });

  const [scrapePricing, setScrapePricing] = useState<any[]>([]);
  const [isScrapeSaving, setIsScrapeSaving] = useState(false);
  const [defaultSignupCredits, setDefaultSignupCredits] = useState<number>(10);
  const [isDefaultCreditsSaving, setIsDefaultCreditsSaving] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPricing();
    fetchScrapePricing();
    fetchDefaultSignupCredits();
  }, []);

  const fetchPricing = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_URL}/api/admin/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPricing(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch pricing', err);
      setError('Could not load pricing data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScrapePricing = async () => {
    try {
      const res = await fetch(`${API_URL}/api/credits/scrape-pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setScrapePricing(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch scrape pricing', err);
    }
  };

  const fetchDefaultSignupCredits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/default-credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDefaultSignupCredits(data.data.defaultCredits);
      }
    } catch (err) {
      console.error('Failed to fetch default signup credits', err);
    }
  };

  const handleSaveDefaultCredits = async () => {
    try {
      setIsDefaultCreditsSaving(true);
      setError('');
      setSuccess('');
      const res = await fetch(`${API_URL}/api/admin/settings/default-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ defaultCredits: defaultSignupCredits })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Default signup credits updated successfully');
      } else {
        setError(data.message || 'Failed to update default signup credits');
      }
    } catch (err) {
      setError('Connection error while saving');
    } finally {
      setIsDefaultCreditsSaving(false);
    }
  };

  const handleSaveScrapePricing = async (id: string, costPerUnit: number, label: string) => {
    try {
      setIsScrapeSaving(true);
      setError('');
      setSuccess('');
      
      const res = await fetch(`${API_URL}/api/admin/scrape-pricing/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ costPerUnit, label })
      });
      
      const data = await res.json();
      if (data.success) {
        setSuccess('Scrape pricing updated successfully');
        fetchScrapePricing();
      } else {
        setError(data.message || 'Failed to update scrape pricing');
      }
    } catch (err) {
      setError('Connection error while saving');
    } finally {
      setIsScrapeSaving(false);
    }
  };

  const handleSave = async (entry: Partial<PricingEntry>) => {
    try {
      setIsSaving(true);
      setError('');
      setSuccess('');
      
      const res = await fetch(`${API_URL}/api/admin/pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(entry)
      });
      
      const data = await res.json();
      if (data.success) {
        setSuccess(`Pricing for ${entry.currencyCode} updated successfully`);
        fetchPricing();
        if (!entry.id) {
           setNewEntry({ currencyCode: '', currencySymbol: '', pricePerCredit: 0.12, isDefault: false });
        }
      } else {
        setError(data.message || 'Failed to save pricing');
      }
    } catch (err) {
      setError('Connection error while saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this currency pricing?')) return;
    
    try {
      setIsSaving(true);
      const res = await fetch(`${API_URL}/api/admin/pricing/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPricing(pricing.filter(p => p.id !== id));
        setSuccess('Pricing removed');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to delete');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="p-8 text-white">Loading pricing settings...</div>;

  return (
    <div className="space-y-8 max-w-5xl pb-20">
      <div>
        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
          Pricing & Credits
        </h1>
        <p className="text-gray-400 mt-2">Manage currency exchange rates and scraping credit costs.</p>
      </div>

      {(error || success) && (
        <div className={`p-4 rounded-xl border ${error ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'}`}>
          {error || success}
        </div>
      )}

      {/* Scrape Action Pricing Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
            <Coins className="text-amber-400" />
            <h3 className="text-xl font-bold text-white">Scrape Credit Costs</h3>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-3xl overflow-hidden">
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {scrapePricing.map((p) => (
                    <ScrapePricingCard key={p.id} item={p} onSave={handleSaveScrapePricing} />
                ))}
            </div>
            {isScrapeSaving && (
                <div className="px-6 py-2 bg-amber-500/10 text-amber-500 text-[10px] font-bold uppercase tracking-widest animate-pulse">
                    Saving changes...
                </div>
            )}
        </div>
      </div>

      <hr className="border-white/5" />

      {/* New User Signup Credits Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
            <Coins className="text-violet-400" />
            <h3 className="text-xl font-bold text-white">New User Signup Settings</h3>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-3xl p-6 md:p-8 space-y-4 shadow-2xl relative">
            <div className="max-w-md space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1">
                    Default Signup Credits
                </label>
                <div className="flex items-center gap-4">
                    <input 
                        type="number"
                        min="0"
                        step="1"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono outline-none focus:border-violet-500/50"
                        value={defaultSignupCredits}
                        onChange={(e) => setDefaultSignupCredits(parseInt(e.target.value) || 0)}
                    />
                    <button 
                        onClick={handleSaveDefaultCredits}
                        disabled={isDefaultCreditsSaving}
                        className="px-6 py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                        <Save size={18} /> {isDefaultCreditsSaving ? 'Saving...' : 'Save Setting'}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Credits automatically allocated to all newly registered users (via Email signup or Google OAuth).
                </p>
            </div>
        </div>
      </div>

      <hr className="border-white/5" />

      {/* Currency Pricing Table */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
            <Plus className="text-purple-400" />
            <h3 className="text-xl font-bold text-white">Currency & Purchasing</h3>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <table className="w-full text-left">
            <thead className="bg-white/5 border-b border-white/10">
                <tr>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Currency</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Symbol</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Price / Credit</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
                {pricing.map((p) => (
                <tr key={p.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-white">{p.currencyCode}</span>
                        {p.isDefault && (
                        <span className="px-2 py-0.5 bg-violet-600/20 text-violet-400 text-[10px] rounded-full font-bold border border-violet-600/20 uppercase">Default</span>
                        )}
                    </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-300">{p.currencySymbol}</td>
                    <td className="px-6 py-4 font-mono text-purple-400">{p.pricePerCredit}</td>
                    <td className="px-6 py-4 text-right">
                    {!p.isDefault && (
                        <button 
                        onClick={() => handleDelete(p.id)}
                        className="p-2 hover:bg-red-500/10 text-gray-500 hover:text-red-400 rounded-lg transition-colors"
                        >
                        <Trash2 size={16} />
                        </button>
                    )}
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
      </div>

      {/* Add New Section */}
      <div className="bg-[#111] border border-white/10 rounded-3xl p-8 space-y-6">
        <div className="flex items-center gap-3">
            <Plus className="text-purple-400" />
            <h3 className="text-xl font-bold text-white">Add Custom Purchasing Rate</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Select Currency</label>
            <select 
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 outline-none transition-all appearance-none cursor-pointer"
              value={newEntry.currencyCode}
              onChange={(e) => {
                const selected = CURRENCY_OPTIONS.find(c => c.code === e.target.value);
                if (selected) {
                  setNewEntry({
                    ...newEntry, 
                    currencyCode: selected.code,
                    currencySymbol: selected.symbol
                  });
                } else {
                  setNewEntry({...newEntry, currencyCode: e.target.value});
                }
              }}
            >
              <option value="" className="bg-[#111]">Choose Currency...</option>
              {CURRENCY_OPTIONS.map(c => (
                <option key={c.code} value={c.code} className="bg-[#111]">
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Symbol</label>
            <input 
              type="text" 
              readOnly
              disabled
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-gray-400 cursor-not-allowed outline-none opacity-60"
              value={newEntry.currencySymbol}
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Price / Credit</label>
            <input 
              type="number" 
              step="0.01"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 outline-none transition-all"
              value={newEntry.pricePerCredit}
              onChange={(e) => setNewEntry({...newEntry, pricePerCredit: parseFloat(e.target.value)})}
            />
          </div>
          <div className="flex items-end">
             <button 
                onClick={() => handleSave(newEntry)}
                disabled={isSaving || !newEntry.currencyCode}
                className="w-full h-[50px] bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
             >
                <Save size={18} /> {isSaving ? 'Saving...' : 'Add Pricing'}
             </button>
          </div>
        </div>
        
        <div className="p-4 bg-white/5 border border-white/5 rounded-2xl flex items-center gap-4 text-xs text-gray-400 font-medium">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                <Coins size={16} className="text-gray-500" />
            </div>
            <p>Currencies not listed here will automatically use the <span className="text-white">Default Currency</span> rate converted via real-time exchange API (Frankfurter).</p>
        </div>
      </div>
    </div>
  );
}
